import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Breadcrumb from "@/components/Breadcrumb";
import SEO from "@/components/SEO";
import AdSlot from "@/components/AdSlot";
import axios from "axios";
import { Calendar, User, Twitter, Facebook, Linkedin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => nav("/404", { replace: true }))
      .finally(() => setLoading(false));
  }, [slug, nav]);

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-slate-500">Loading…</div>;
  if (!post) return null;

  const url = window.location.href;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEO
        title={`${post.title} — ImageKitHub Blog`}
        description={post.excerpt}
        canonical={url}
        image={post.cover_image}
        jsonLd={[{
          "@context": "https://schema.org", "@type": "Article",
          headline: post.title, description: post.excerpt, image: post.cover_image,
          author: { "@type": "Person", name: post.author }, datePublished: post.created_at, dateModified: post.updated_at,
        }]}
      />
      <Breadcrumb items={[{ label: "Blog", to: "/blog" }, { label: post.title }]} />

      <article className="mt-6">
        <span className="text-xs font-semibold uppercase tracking-widest text-blue-600 dark:text-blue-400">{post.category}</span>
        <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 dark:text-white leading-tight">{post.title}</h1>
        <div className="mt-4 flex items-center gap-4 text-sm text-slate-500">
          <span className="flex items-center gap-1"><User size={12} /> {post.author}</span>
          <span className="flex items-center gap-1"><Calendar size={12} /> {new Date(post.created_at).toLocaleDateString()}</span>
        </div>
        {post.cover_image && <img src={post.cover_image} alt={post.title} className="mt-8 rounded-2xl w-full max-h-96 object-cover" />}
        <div className="mt-10 prose prose-slate dark:prose-invert max-w-none text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
          {post.content}
        </div>

        <AdSlot label="Sponsored" />

        <div className="mt-10 flex items-center gap-3 border-t border-slate-200 dark:border-white/10 pt-6">
          <span className="text-xs uppercase tracking-widest text-slate-500">Share:</span>
          <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(post.title)}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg border border-slate-200 dark:border-white/10 hover:text-blue-600"><Twitter size={16} /></a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg border border-slate-200 dark:border-white/10 hover:text-blue-600"><Facebook size={16} /></a>
          <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg border border-slate-200 dark:border-white/10 hover:text-blue-600"><Linkedin size={16} /></a>
        </div>

        <div className="mt-10">
          <Link to="/blog" className="text-blue-600 dark:text-blue-400 text-sm hover:underline">← Back to all posts</Link>
        </div>
      </article>
    </div>
  );
}
